import { useQuery } from "@vue/apollo-composable";
import type { ComputedRef } from "vue";
import type { LocationQueryRaw } from "vue-router";
import { TenancyFilterBookmarksDocument, CanvasLayoutsForRouteDocument, CanvasLayoutFieldsFragmentDoc } from "~/graphql/generated/graphql";
import { useFragment } from "~/graphql/generated";
import { getTenancyDefaultUrlQuery } from "~/helpers/apollo/apollo-portfolio-filter-helpers";
import { isStandardSetting, settingsTemplateOptionsTranslationKeys } from "~/helpers/modules/opex-helpers";
import { REPORTS_CANVAS_ROUTE } from "~/composables/canvas/useReportsCanvas";

export interface NavItem {
  type: MenuItemType;
  iconId?: string;
  position: number;
  labelText: string;
  active: ComputedRef<boolean>;
  route?: { name: string; params?: Record<string, string>; query?: LocationQueryRaw };
  onClick?: () => void;
  children?: NavItem[];
  badge?: string;
  warningBadge?: boolean;
  warningMessage?: string;
}

export enum MenuItemType {
  SEARCH = "SEARCH",
  HOME = "HOME",
  ASSETS = "ASSETS",
  ANALYTICS = "ANALYTICS",
  ACCOUNTS = "ACCOUNTS",
  SETTINGS = "SETTINGS",
  DATA_MANAGEMENT = "DATA_MANAGEMENT",
  DATA_MAPPING = "DATA_MAPPING",
  INTEGRATION_HUB = "INTEGRATION_HUB",
  PIPELINES = "PIPELINES",
  RENT_ROLL = "RENT_ROLL",
  OPEX_TRACKER = "OPEX_TRACKER",
  REPORTS = "REPORTS",
}

/** Extracts badge props from a warning entry, if any. */
function warningBadge(warnings: ReturnType<typeof useMenuStore>["warnings"], type: MenuItemType) {
  const warning = warnings.find((w) => w.menuItem === type);
  const count = warning?.count ?? 0;
  return {
    badge: count > 0 ? count.toString() : undefined,
    warningBadge: count > 0,
    warningMessage: warning?.message,
  };
}

export const useChromeMenuItems = () => {
  const route = useRoute();
  const t = useTranslator();
  const filter = useFilter();
  const isFeatureSupported = useIsFeatureSupported();
  const filterBookmarksQuery = useQuery(TenancyFilterBookmarksDocument);
  const opexPreferencesQuery = usePreferencesNamespace("preferencesNamespaceOpex");
  const opexBookmarksQuery = useOpexSavedTemplates(true);
  const ampli = useAmplitude();
  const menuStore = useMenuStore();
  const chromeStore = useChromeStore();
  const hasIntegrationRead = useHasPermissions(PERMISSIONS.INTEGRATION_READ);
  const hasReportsRead = useHasPermissions(PERMISSIONS.REPORTS_READ);

  const filterBookmarks = computed(() => filterBookmarksQuery.result.value?.assetManagementTenancyFilterBookmarks);
  const defaultFilter = computed(() => filterBookmarks.value?.find((f) => f.isDefault));
  const opexBookmarks = computed(() => opexBookmarksQuery.result.value);
  const reportsQuery = useQuery(CanvasLayoutsForRouteDocument, { route: REPORTS_CANVAS_ROUTE }, { fetchPolicy: "cache-first" });
  const reportsLayouts = computed(() => {
    const raw = reportsQuery.result.value?.canvasLayoutsForRoute ?? [];
    return raw.map((entry) => {
      const layout = useFragment(CanvasLayoutFieldsFragmentDoc, entry);
      return { id: layout.id, name: layout.name || layout.id, isDefault: layout.isDefault };
    });
  });
  const loading = computed(() => filterBookmarksQuery.loading.value || opexBookmarksQuery.loading.value);

  // ── Home section: search, home, assets ──────────────────────────────────────

  const homeItems = computed<NavItem[]>(() => [
    {
      type: MenuItemType.SEARCH,
      iconId: "search",
      position: 0,
      labelText: t("SIDE_MENU_ITEM_SEARCH"),
      children: undefined,
      active: computed(() => false),
      route: undefined,
      onClick: () => chromeStore.toggleSearch(),
    },
    {
      type: MenuItemType.HOME,
      iconId: "home",
      position: 1,
      labelText: t("SIDE_MENU_ITEM_HOME"),
      children: undefined,
      active: computed(() => route.path === "/"),
      route: { name: "index", query: undefined },
    },
    {
      type: MenuItemType.ASSETS,
      iconId: "location_city",
      position: 2,
      labelText: t("SIDE_MENU_ITEM_ASSETS"),
      children: undefined,
      active: computed(() => route.path.startsWith("/assets")),
      route: { name: "assets", query: undefined },
    },
  ]);

  // ── Module section: reports → opex → rent roll → accounts → analytics ───────

  const moduleItems = computed<NavItem[]>(() => {
    const warnings = menuStore.warnings;
    const items: NavItem[] = [];

    if (hasReportsRead.result.value) {
      const reportChildren: NavItem[] = reportsLayouts.value.length > 0
        ? reportsLayouts.value.map((layout, i) => ({
            type: MenuItemType.REPORTS,
            position: i,
            iconId: undefined,
            labelText: filter.limitCharacters(layout.name, 24),
            active: computed(() => route.path === `/reports/${layout.id}`),
            route: { name: "reports-layoutId", params: { layoutId: layout.id } },
          }))
        : [{
            type: MenuItemType.REPORTS,
            position: 0,
            iconId: undefined,
            labelText: filter.limitCharacters(t("REPORTS_STANDARD_REPORT"), 24),
            active: computed(() => route.path === "/reports/fallback"),
            route: { name: "reports-layoutId", params: { layoutId: "fallback" } },
          }];

      items.push({
        type: MenuItemType.REPORTS,
        iconId: "bar_chart",
        position: 0,
        labelText: t("SIDE_MENU_ITEM_REPORTS"),
        active: computed(() => route.path === "/reports"),
        route: { name: "reports", query: undefined },
        children: reportChildren,
      });
    }

    // Opex Tracker
    items.push({
      type: MenuItemType.OPEX_TRACKER,
      iconId: "rubric",
      position: 1,
      labelText: t("SIDE_MENU_ITEM_OPEX"),
      active: computed(() => route.path.startsWith("/opextracker")),
      route: { name: "opextracker", query: { templateId: opexPreferencesQuery.result.value?.template } },
      children: [...(opexBookmarks.value ?? [])].map((bookmark, i) => ({
        type: MenuItemType.OPEX_TRACKER,
        position: i,
        iconId: undefined,
        labelText: filter.limitCharacters(
          isStandardSetting(bookmark) ? t(settingsTemplateOptionsTranslationKeys[bookmark.id]) : bookmark.name,
          24,
        ),
        active: computed(() => route.path.startsWith("/opextracker") && route.query.templateId === bookmark.id),
        route: { name: "opextracker", query: { ...route.query, templateId: bookmark.id } as LocationQueryRaw },
      })),
    });

    // Rent Roll
    items.push({
      type: MenuItemType.RENT_ROLL,
      iconId: "table_view",
      position: 2,
      labelText: t("SIDE_MENU_ITEM_RENT_ROLL"),
      active: computed(() => route.path.startsWith("/rentroll") && route.query.filterId === defaultFilter.value?.id),
      route: { name: "rentroll", query: { ...getTenancyDefaultUrlQuery(defaultFilter.value?.id) } },
      ...warningBadge(warnings, MenuItemType.RENT_ROLL),
      onClick: () => {
        if (defaultFilter.value) {
          ampli.openSavedRentRoll({ savedFilterId: defaultFilter.value.id, savedFilterName: defaultFilter.value.name });
        }
      },
      children: filterBookmarks.value?.map((bookmark, i) => ({
        type: MenuItemType.RENT_ROLL,
        position: i,
        iconId: undefined,
        labelText: filter.limitCharacters(bookmark.name, 24),
        active: computed(() => route.path.startsWith("/rentroll") && route.query.filterId === bookmark.id),
        route: { name: "rentroll", query: { ...route.query, filterId: bookmark.id } as LocationQueryRaw },
        onClick: () => ampli.openSavedRentRoll({ savedFilterId: bookmark.id, savedFilterName: bookmark.name }),
      })),
    });

    // Accounts
    items.push({
      type: MenuItemType.ACCOUNTS,
      iconId: "account_balance",
      position: 3,
      labelText: t("SIDE_MENU_ITEM_ACCOUNTS"),
      children: undefined,
      active: computed(() => route.path.startsWith("/accounts")),
      route: { name: "accounts", query: undefined },
    });

    // Analytics
    items.push({
      type: MenuItemType.ANALYTICS,
      iconId: "area_chart",
      position: 4,
      labelText: t("SIDE_MENU_ITEM_ANALYTICS"),
      children: undefined,
      active: computed(() => route.path.startsWith("/analytics")),
      route: { name: "analytics", query: undefined },
    });

    return items;
  });

  // ── Settings section ─────────────────────────────────────────────────────────

  const settingsItems = computed<NavItem[]>(() => [
    {
      type: MenuItemType.SETTINGS,
      iconId: "manage_accounts",
      position: 0,
      labelText: t("SIDE_MENU_ITEM_PERSONAL_SETTINGS"),
      children: undefined,
      active: computed(() => route.path.startsWith("/settings")),
      route: { name: "settings", query: undefined },
    },
  ]);

  // ── Data management section (feature-gated) ──────────────────────────────────

  const dataManagementItems = computed<NavItem[]>(() => {
    if (!isFeatureSupported("portfolioDataWarehouse")) return [];

    const warnings = menuStore.warnings;
    const items: NavItem[] = [
      {
        type: MenuItemType.DATA_MAPPING,
        iconId: "category",
        position: 0,
        labelText: t("SIDE_MENU_ITEM_DATA_MAPPING"),
        active: computed(() => route.path.startsWith("/datamanagement/datamapping")),
        route: { name: "datamanagement-datamapping", query: undefined },
        children: undefined,
        ...warningBadge(warnings, MenuItemType.DATA_MAPPING),
      },
      {
        type: MenuItemType.PIPELINES,
        iconId: "graph_1",
        position: 2,
        labelText: t("SIDE_MENU_ITEM_PIPELINES"),
        active: computed(() => route.path.startsWith("/datamanagement/pipelines")),
        route: { name: "datamanagement-pipelines", query: undefined },
        children: undefined,
        ...warningBadge(warnings, MenuItemType.PIPELINES),
      },
    ];

    if (hasIntegrationRead.result.value) {
      items.splice(1, 0, {
        type: MenuItemType.INTEGRATION_HUB,
        iconId: "hub",
        position: 1,
        labelText: t("SIDE_MENU_ITEM_INTEGRATION_HUB"),
        active: computed(() => route.path.startsWith("/datamanagement/integrationhub")),
        route: { name: "datamanagement-integrationhub", query: undefined },
        children: undefined,
        ...warningBadge(warnings, MenuItemType.INTEGRATION_HUB),
      });
    }

    return items;
  });

  return { loading, homeItems, moduleItems, settingsItems, dataManagementItems };
};
